import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to CCNEO Connect");
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (kind) => {
    if (kind === "admin") {
      setEmail("admin@ccneo.org");
      setPassword("CCNEOadmin2026");
    } else {
      setEmail("demo@ccneo.org");
      setPassword("CCNEOdemo2026");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1760960553755-0f355491749e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjb3JuZWxsJTIwdW5pdmVyc2l0eSUyMGNhbXB1cyUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4NjkwMTAxNHww&ixlib=rb-4.1.0&q=85"
          alt="Cornell clock tower"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#B31B1B]/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur grid place-items-center text-xl font-bold" style={{ fontFamily: "Georgia, serif" }}>C</div>
            <div>
              <div className="font-extrabold text-lg">CCNEO Connect</div>
              <div className="text-xs uppercase tracking-widest opacity-80">Cornell · Northeast Ohio</div>
            </div>
          </div>
          <div>
            <h1 className="text-5xl font-extrabold leading-tight max-w-md">Connecting Cornell's past, present & future in Northeast Ohio.</h1>
            <p className="mt-4 max-w-md text-white/85 leading-relaxed">A private community for Cleveland-area Cornell alumni and students to share opportunities, mentor, and stay in touch.</p>
          </div>
          <div className="text-xs opacity-70">Powered by SocialImpactCLE</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8" data-testid="login-brand">
            <div className="w-10 h-10 rounded-xl bg-[#B31B1B] text-white grid place-items-center text-xl font-bold" style={{ fontFamily: "Georgia, serif" }}>C</div>
            <div>
              <div className="font-extrabold text-lg">CCNEO Connect</div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Cornell Club NEO</div>
            </div>
          </Link>

          <h2 className="text-3xl font-extrabold tracking-tight">Welcome back</h2>
          <p className="mt-2 text-muted-foreground">Sign in to your CCNEO Connect account.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@cornell.edu"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-[#B31B1B] bg-[#B31B1B]/5 border border-[#B31B1B]/20 px-3 py-2 rounded-md" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full bg-[#B31B1B] hover:bg-[#8f1616] rounded-full h-11"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-center text-muted-foreground">
            New to CCNEO?{" "}
            <Link to="/register" className="font-semibold text-[#B31B1B] hover:underline" data-testid="login-to-register-link">
              Create an account
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-xs uppercase tracking-widest text-muted-foreground text-center">Demo access</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => fillDemo("admin")} data-testid="login-fill-admin-btn">
                Admin
              </Button>
              <Button variant="outline" onClick={() => fillDemo("member")} data-testid="login-fill-member-btn">
                Member
              </Button>
            </div>
          </div>
          <p className="mt-8 text-center text-[10px] text-muted-foreground lg:hidden">Powered by SocialImpactCLE</p>
        </div>
      </div>
    </div>
  );
}
